<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link :to="`/app/tests/edit/${testId}`" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <!-- Stats Cards -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">
          <vue-feather type="users" size="20" />
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.totalAttempts }}</span>
          <span class="stat-label">Total Attempts</span>
        </div>
      </div>
      <div class="stat-card stat-success">
        <div class="stat-icon">
          <vue-feather type="check-circle" size="20" />
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.passed }}</span>
          <span class="stat-label">Passed</span>
        </div>
      </div>
      <div class="stat-card stat-danger">
        <div class="stat-icon">
          <vue-feather type="x-circle" size="20" />
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.failed }}</span>
          <span class="stat-label">Failed</span>
        </div>
      </div>
      <div class="stat-card stat-info">
        <div class="stat-icon">
          <vue-feather type="trending-up" size="20" />
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.avgScore }}%</span>
          <span class="stat-label">Avg Score</span>
        </div>
      </div>
    </div>

    <DataTable
      :items="sessions"
      :columns="columns"
      :loading="loading"
      :searchable="false"
      empty-icon="bar-chart-2"
      empty-title="No results yet"
      empty-description="Results will appear here once users complete the test"
    >
      <template #cell-user="{ item }">
        <span class="fw-medium">{{ item.userName || item.userEmail }}</span>
      </template>

      <template #cell-started="{ item }">
        <span class="text-muted">{{ formatDate(item.startedAt) }}</span>
      </template>

      <template #cell-timeTaken="{ item }">
        <span class="fw-medium">{{ formatDuration(item.timeTaken) }}</span>
      </template>

      <template #cell-score="{ item }">
        <div class="score-display">
          <span :class="item.percentage >= (test?.passingMarks || test?.passing_marks || 0) ? 'text-success' : 'text-danger'">
            {{ item.totalScore }}/{{ test?.totalMarks || test?.total_marks || '-' }}
          </span>
          <span class="score-percentage" :class="item.percentage >= (test?.passingMarks || test?.passing_marks || 0) ? 'bg-success' : 'bg-danger'">
            {{ item.percentage }}%
          </span>
        </div>
      </template>

      <template #cell-status="{ item }">
        <span :class="['status-badge', getStatusClass(item.status)]">{{ item.status.replace('_', ' ') }}</span>
      </template>

      <template #cell-actions="{ item }">
        <div class="action-buttons">
          <button 
            class="action-btn action-view" 
            @click="viewResponses(item)" 
            v-tooltip="'View responses'"
          >
            <vue-feather type="eye" size="14" />
          </button>
        </div>
      </template>
    </DataTable>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import DataTable from '@/components/data-table/DataTable.vue'

export default {
  name: 'TestResults',
  components: { DataTable },
  setup() {
    const route = useRoute()
    const testId = route.params.testId

    const columns = [
      { key: 'user', label: 'User', sortable: true, minWidth: '200px' },
      { key: 'started', label: 'Started', sortable: true },
      { key: 'timeTaken', label: 'Time Taken', sortable: true },
      { key: 'score', label: 'Score', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    const test = ref(null)
    const sessions = ref([])
    const loading = ref(true)
    const stats = ref({ totalAttempts: 0, passed: 0, failed: 0, avgScore: 0 })

    const breadcrumbs = computed(() => {
      const items = [
        { title: 'Dashboard', url: '/app/dashboard' },
        { title: 'Exams', url: '/app/exams/list' }
      ]
      // Add parent context based on test type
      if (test.value?.lessonId) {
        // Exam → Subject → Topic → Lesson → Test → Results
        if (test.value?.examId) {
          items.push({ title: test.value?.examName || 'Exam', url: `/app/exams/${test.value.examId}/subjects` })
        }
        if (test.value?.subjectId) {
          items.push({ title: test.value?.subjectName || 'Subject', url: `/app/subjects/${test.value.subjectId}/topics` })
        }
        if (test.value?.topicId) {
          items.push({ title: test.value?.topicName || 'Topic', url: `/app/topics/${test.value.topicId}/lessons` })
        }
        items.push({ title: test.value?.lessonTitle || 'Lesson', url: `/app/lessons/${test.value.lessonId}/tests` })
      } else if (test.value?.seriesId) {
        // Exam → TestSeries → Test → Results
        if (test.value?.examId) {
          items.push({ title: test.value?.examName || 'Exam', url: `/app/exams/${test.value.examId}/test-series` })
        }
        items.push({ title: test.value?.seriesName || 'Test Series', url: `/app/test-series/${test.value.seriesId}/tests` })
      } else if (test.value?.topicId) {
        // Exam → Subject → Topic → Test → Results
        if (test.value?.examId) {
          items.push({ title: test.value?.examName || 'Exam', url: `/app/exams/${test.value.examId}/subjects` })
        }
        if (test.value?.subjectId) {
          items.push({ title: test.value?.subjectName || 'Subject', url: `/app/subjects/${test.value.subjectId}/topics` })
        }
        items.push({ title: test.value?.topicName || 'Topic', url: `/app/topics/${test.value.topicId}/tests` })
      }
      items.push({ title: test.value?.title || 'Test', url: `/app/tests/${testId}/questions` })
      items.push({ title: 'Results', active: true })
      return items
    })

    const fetchData = async () => {
      loading.value = true
      try {
        const [testRes, sessionsRes] = await Promise.all([
          api.get(`/v1/admin/tests/${testId}`),
          api.get(`/v1/admin/tests/${testId}/sessions`)
        ])
        test.value = testRes.data.test
        const rawSessions = sessionsRes.data.sessions || []
        
        // Map API response fields to what the template expects
        sessions.value = rawSessions.map(s => {
          const totalQuestions = (s.totalCorrect || 0) + (s.totalWrong || 0)
          const totalMarks = test.value?.totalMarks || test.value?.total_marks || totalQuestions
          const score = s.score ?? 0
          const percentage = totalMarks > 0 ? Math.round((score / totalMarks) * 100) : 0
          
          // Calculate time taken in seconds from startTime and submitTime
          let timeTaken = s.timeTaken || null
          if (!timeTaken && s.startTime && s.submitTime) {
            timeTaken = Math.round((new Date(s.submitTime) - new Date(s.startTime)) / 1000)
          }

          return {
            ...s,
            userName: s.userName || s.userEmail || s.userId || 'Unknown',
            startedAt: s.startedAt || s.startTime || s.createdAt,
            totalScore: s.totalScore ?? s.score ?? 0,
            totalCorrect: s.totalCorrect || 0,
            totalWrong: s.totalWrong || 0,
            percentage,
            timeTaken
          }
        })
        
        const completed = sessions.value.filter(s => s.status === 'completed')
        const passingMarks = test.value?.passingMarks || test.value?.passing_marks || 0
        stats.value = {
          totalAttempts: sessions.value.length,
          passed: completed.filter(s => s.percentage >= passingMarks).length,
          failed: completed.filter(s => s.percentage < passingMarks).length,
          avgScore: completed.length ? Math.round(completed.reduce((a, s) => a + s.percentage, 0) / completed.length) : 0
        }
      } catch (err) {
        console.error('Failed to fetch data:', err)
      } finally {
        loading.value = false
      }
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatDuration = (seconds) => {
      if (!seconds) return '-'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}m ${secs}s`
    }

    const getStatusClass = (status) => {
      const classes = { 
        in_progress: 'status-pending', 
        completed: 'status-active', 
        abandoned: 'status-error' 
      }
      return classes[status] || 'status-inactive'
    }

    const viewResponses = (session) => {
      console.log('View responses for session:', session.id)
    }

    onMounted(fetchData)

    return { 
      testId, 
      columns,
      test, 
      sessions, 
      loading,
      stats, 
      breadcrumbs, 
      formatDate, 
      formatDuration, 
      getStatusClass, 
      viewResponses 
    }
  }
}
</script>

<style scoped>
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

@media (max-width: 992px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.stat-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #f1f5f9;
  border-radius: 12px;
  color: #64748b;
}

.stat-card.stat-success .stat-icon {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.stat-card.stat-danger .stat-icon {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.stat-card.stat-info .stat-icon {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
}

.score-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.score-percentage {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  color: #fff;
}

:global(body.dark-mode) .stat-card {
  background: #1e293b;
  border-color: #334155;
}

:global(body.dark-mode) .stat-icon {
  background: #334155;
  color: #94a3b8;
}

:global(body.dark-mode) .stat-value {
  color: #e2e8f0;
}
</style>
